import { DownOutlined } from '@ant-design/icons';
import { Dropdown, message, Switch } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import { withUserType } from '../withUserType';
import { CONTRACTOR_PROPS, ROLE_TYPES } from '../../utils/DataConstants';
import { connect } from 'react-redux';
import {
	addBuyerSubscribersForBuyers,
	addContractorSubscribers,
	addSubSubscribers,
	addSupplierSubscribersForSuppliers,
	removeBuyerSubscriberForBuyers,
	removeContractorSubscribers,
	removeSubSubscribers,
	removeSupplierSubscriberForSuppliers,
} from '../../thunks/work_orders_subscribers_thunks';
import { USER_NOTIFICATIONS_CRUD_ACTION_CREATORS } from '../../actions/user_notifications_actions';
import {
	getNotificationDetailsFromKey,
	getNotificationLabelFromDetails,
	getSubscribeObjectForWorkOrder,
} from '../../utils/NotificationUtils';
import { buyerSettingsRestCrudThunksForBuyer } from '../../thunks/buyer_settings_thunks';
import { supplierSettingsRestCrudThunksForSupplier } from '../../thunks/supplier_settings_thunks';
import {
	workOrdersRestCrudThunksForBuyer,
	workOrdersRestCrudThunksForSupplier,
} from '../../thunks/work_orders_thunks';

const EachNotificationSetting = ({
	title,
	saving,
	description,
	isSubscribed,
	onChange,
	disabled = false,
	workOrder,
	getWorkOrder,
}) => {
	const modifiedIsSubscribed = useMemo(
		() =>
			//When the WO notificaiton is turned off, others will be disabled
			//When it is disabled it should be turned off as well
			disabled ? false : isSubscribed,
		// isSubscribed,
		[disabled, isSubscribed]
	);

	const isPrivateWorkOrderAndUnsubscribed = useMemo(
		() => nullSafeGet('isPrivate', workOrder) && !isSubscribed,
		[isSubscribed, workOrder]
	);

	const revisedDisabled = useMemo(
		() => disabled || isPrivateWorkOrderAndUnsubscribed,
		[disabled, isPrivateWorkOrderAndUnsubscribed]
	);

	return (
		<div
			className={`flex w-full flex-row items-center justify-between ${
				revisedDisabled ? 'opacity-60' : ''
			}`}
		>
			<div className="flex flex-col items-start">
				<div className="bodymedium">
					{title}
					{/* {isPrivateWorkOrderAndUnsubscribed ? <span className='bodyregular ml-2 opacity-45'>{`(Private)`}</span> : null} */}
				</div>
				<div className="bodyregular mt-2 opacity-45">{description}</div>
			</div>
			<div className="ml-6 flex min-w-[80px] flex-col items-center justify-center">
				<div
					className={`footnoteMedium`}
					style={{ color: modifiedIsSubscribed ? '#389E0D' : '#FF4D4F' }}
				>
					{modifiedIsSubscribed ? 'Subscribed' : 'Unsubscribed'}
				</div>
				<div onClick={stopDefaultEvents}>
					<Switch
						size="small"
						loading={saving}
						disabled={revisedDisabled}
						checked={modifiedIsSubscribed}
						onChange={onChange}
					/>
				</div>
			</div>
		</div>
	);
};

const NOTIFICATION_TYPE = {
	email: 'email',
	inApp: 'inApp',
	workOrder: 'workOrder',
	subContractedWorkOrder: 'subContractedWorkOrder',
};

const NotificationSubscriptionHandler: FC<any> = ({
	notification,
	userType,
	currentUser,
	subscribeToWO,
	unsubscribeFromWO,
	updateNotificaitonObjectInRedux,
	settings,
	updateSettings,
	subscribeToSubContractedWO,
	unsubscribeToSubContractedWO,
	getWorkOrder,
}): React.ReactElement => {
	const [saving, setSaving] = useState(null);

	const woAvailable = useMemo(() => !!nullSafeGet('workOrder', notification), [notification]);

	const subscriberList = useMemo(() => {
		return userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse(
					`workOrder.${CONTRACTOR_PROPS.FROM_WO}.subSubscriberEmails`,
					notification,
					nullSafeGetOrElse(`workOrder.supplierSubscriberEmails`, notification, [])
			  )
			: nullSafeGetOrElse('workOrder.buyerSubscriberEmails', notification, []);
	}, [notification, userType]);

	const isWOSubscribed = useMemo(
		() => subscriberList.includes(nullSafeGetOrElse('email', currentUser, '')),
		[currentUser, subscriberList]
	);

	const subContractedSubscriberList = useMemo(() => {
		return userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse(
					`workOrder.${CONTRACTOR_PROPS.TO_WO}.contractorSubscriberEmails`,
					notification,
					[]
			  )
			: [];
	}, [notification, userType]);

	const isSubContractedWOSubscribed = useMemo(
		() => subContractedSubscriberList.includes(nullSafeGetOrElse('email', currentUser, '')),
		[currentUser, subContractedSubscriberList]
	);

	const subContractedToWOAvailable = useMemo(
		() => !!nullSafeGet(`workOrder.${CONTRACTOR_PROPS.TO_WO}.id`, notification),
		[notification]
	);

	const isSubContractedFromWO = useMemo(
		() => !!nullSafeGet(`workOrder.${CONTRACTOR_PROPS.FROM_WO}.id`, notification),
		[notification]
	);

	const workOrderId = useMemo(
		() =>
			isSubContractedFromWO
				? nullSafeGet(`workOrder.${CONTRACTOR_PROPS.FROM_WO}.id`, notification)
				: nullSafeGet('workOrder.id', notification),
		[isSubContractedFromWO, notification]
	);

	const subscribeObj = useMemo(
		() =>
			getSubscribeObjectForWorkOrder(
				isWOSubscribed,
				workOrderId,
				currentUser,
				isSubContractedFromWO
			),
		[currentUser, isSubContractedFromWO, isWOSubscribed, workOrderId]
	);

	const onSubscribe = useCallback(() => {
		setSaving(NOTIFICATION_TYPE.workOrder);
		subscribeToWO(subscribeObj, nullSafeGet('workOrder', notification), isSubContractedFromWO)
			.then((workOrder) =>
				updateNotificaitonObjectInRedux({
					id: notification.id,
					workOrder: {
						...workOrder,
						[CONTRACTOR_PROPS.TO_WO]: nullSafeGetOrElse(
							`workOrder.${CONTRACTOR_PROPS.TO_WO}`,
							notification,
							{}
						),
						[CONTRACTOR_PROPS.FROM_WO]: nullSafeGetOrElse(
							`${CONTRACTOR_PROPS.TO_WO}`,
							workOrder,
							{}
						),
					},
				})
			)
			.catch(message.error)
			.finally(() => setSaving(null));
	}, [
		subscribeToWO,
		subscribeObj,
		notification,
		isSubContractedFromWO,
		updateNotificaitonObjectInRedux,
	]);

	const onUnSubscribe = useCallback(() => {
		setSaving(NOTIFICATION_TYPE.workOrder);
		unsubscribeFromWO(subscribeObj, nullSafeGet('workOrder', notification), isSubContractedFromWO)
			.then((workOrder) =>
				updateNotificaitonObjectInRedux({
					id: notification.id,
					workOrder: {
						...workOrder,
						[CONTRACTOR_PROPS.TO_WO]: nullSafeGetOrElse(
							`workOrder.${CONTRACTOR_PROPS.TO_WO}`,
							notification,
							{}
						),
						[CONTRACTOR_PROPS.FROM_WO]: nullSafeGetOrElse(
							`${CONTRACTOR_PROPS.TO_WO}`,
							workOrder,
							{}
						),
					},
				})
			)
			.catch(message.error)
			.finally(() => setSaving(null));
	}, [
		isSubContractedFromWO,
		notification,
		subscribeObj,
		unsubscribeFromWO,
		updateNotificaitonObjectInRedux,
	]);

	const subscribeObjForSubContractedWO = useMemo(() => {
		const prefix = isSubContractedWOSubscribed ? 'remove' : 'add';
		return {
			id: parseInt(nullSafeGet(`workOrder.${CONTRACTOR_PROPS.TO_WO}.id`, notification)),
			[`${prefix}ContractorSubscriberEmails`]: [currentUser.email],
		};
	}, [currentUser.email, isSubContractedWOSubscribed, notification]);

	const onSubscribeSubContractedWO = useCallback(() => {
		setSaving(NOTIFICATION_TYPE.subContractedWorkOrder);
		subscribeToSubContractedWO(
			subscribeObjForSubContractedWO,
			nullSafeGet('workOrder', notification)
		)
			.then((workOrder) =>
				updateNotificaitonObjectInRedux({
					id: parseInt(notification.id),
					workOrder,
				})
			)
			.catch(message.error)
			.finally(() => setSaving(null));
	}, [
		notification,
		subscribeObjForSubContractedWO,
		subscribeToSubContractedWO,
		updateNotificaitonObjectInRedux,
	]);

	const onUnSubscribeSubContractedWO = useCallback(() => {
		setSaving(NOTIFICATION_TYPE.subContractedWorkOrder);
		unsubscribeToSubContractedWO(
			subscribeObjForSubContractedWO,
			nullSafeGet('workOrder', notification)
		)
			.then((workOrder) =>
				updateNotificaitonObjectInRedux({
					id: parseInt(notification.id),
					workOrder,
				})
			)
			.catch(message.error)
			.finally(() => setSaving(null));
	}, [
		notification,
		subscribeObjForSubContractedWO,
		unsubscribeToSubContractedWO,
		updateNotificaitonObjectInRedux,
	]);

	const notificationDetails = useMemo(
		() => getNotificationDetailsFromKey(userType)(nullSafeGet('notificationType', notification)),
		[notification, userType]
	);

	const existingRootNotificationConfig = useMemo(
		() =>
			notificationDetails && nullSafeGetOrElse(notificationDetails.notificationKey, settings, {}),
		[settings, notificationDetails]
	);

	const existingNotificationConfig = useMemo(
		() =>
			notificationDetails && nullSafeGet(notificationDetails.key, existingRootNotificationConfig),
		[existingRootNotificationConfig, notificationDetails]
	);

	const emailSubscribed = useMemo(
		() => (existingNotificationConfig ? existingNotificationConfig.email !== false : true),
		[existingNotificationConfig]
	);

	const inAppSubscribed = useMemo(
		() => (existingNotificationConfig ? existingNotificationConfig.inApp !== false : true),
		[existingNotificationConfig]
	);

	const handleBuyerSettingsChange = useCallback(
		(subscriptionType, value) => {
			const newSettings = {
				...settings,
				[notificationDetails.notificationKey]: {
					...existingRootNotificationConfig,
					[notificationDetails.key]: {
						...(existingNotificationConfig || {}),
						[subscriptionType]: value,
					},
				},
			};
			setSaving(subscriptionType);
			updateSettings(newSettings)
				.catch(message.error)
				.finally(() => setSaving(null));
		},
		[
			settings,
			existingNotificationConfig,
			existingRootNotificationConfig,
			notificationDetails,
			updateSettings,
		]
	);

	const onSubscriptionToggle = useCallback(
		(type) => (value) => {
			switch (type) {
				case NOTIFICATION_TYPE.workOrder:
					value ? onSubscribe() : onUnSubscribe();
					break;
				case NOTIFICATION_TYPE.subContractedWorkOrder:
					value ? onSubscribeSubContractedWO() : onUnSubscribeSubContractedWO();
					break;
				default:
					handleBuyerSettingsChange(type, value);
					break;
			}
		},
		[
			handleBuyerSettingsChange,
			onSubscribe,
			onSubscribeSubContractedWO,
			onUnSubscribe,
			onUnSubscribeSubContractedWO,
		]
	);

	const notificationLabel = useMemo(
		() =>
			notificationDetails &&
			`Notifications for ${getNotificationLabelFromDetails(notificationDetails)}`,
		[notificationDetails]
	);

	const anyWOSubscribed = useMemo(
		() => isWOSubscribed || isSubContractedWOSubscribed,
		[isSubContractedWOSubscribed, isWOSubscribed]
	);

	const notificationStatusProps = useMemo(() => {
		if (!anyWOSubscribed)
			return {
				label: 'WO notifications OFF',
				iconName: 'notification_bell_with_strike',
				iconColor: '#FF4D4F',
			};

		return emailSubscribed && inAppSubscribed
			? {
					label: 'All notifications ON',
					iconName: 'notification_bell_with_tick',
					iconColor: '#389E0D',
			  }
			: emailSubscribed
			? {
					label: 'Email ON, Mobile Push OFF',
					iconName: 'notification_email_with_tick',
					iconColor: '#389E0D',
			  }
			: inAppSubscribed
			? {
					label: 'Mobile Push ON, Email OFF',
					iconName: 'notification_mobile_with_tick',
					iconColor: '#389E0D',
			  }
			: {
					label: 'Email & Mobile Push OFF',
					iconName: 'notification_mobile_cancel',
					icon2Name: 'notification_email_cancel',
					iconColor: '#FA8C16',
			  };
	}, [anyWOSubscribed, emailSubscribed, inAppSubscribed]);

	const DropdownContent = useMemo(() => {
		return (
			<div className="flex flex-col items-start gap-4 bg-white p-4 shadow-notifications-card">
				{notificationLabel ? (
					<>
						<EachNotificationSetting
							title="Email"
							description={notificationLabel}
							isSubscribed={emailSubscribed}
							saving={saving === NOTIFICATION_TYPE.email}
							disabled={!anyWOSubscribed}
							onChange={onSubscriptionToggle(NOTIFICATION_TYPE.email)}
						/>
						<EachNotificationSetting
							title="Mobile Push"
							description={notificationLabel}
							isSubscribed={inAppSubscribed}
							saving={saving === NOTIFICATION_TYPE.inApp}
							disabled={!anyWOSubscribed}
							onChange={onSubscriptionToggle(NOTIFICATION_TYPE.inApp)}
						/>
					</>
				) : null}
				{woAvailable ? (
					<EachNotificationSetting
						title={`WO #${nullSafeGetOrElse('workOrder.id', notification, '--')}`}
						description="Email & mobile push notifications for this work order"
						isSubscribed={isWOSubscribed}
						workOrder={nullSafeGet('workOrder', notification)}
						saving={saving === NOTIFICATION_TYPE.workOrder}
						onChange={onSubscriptionToggle(NOTIFICATION_TYPE.workOrder)}
					/>
				) : null}
				{subContractedToWOAvailable ? (
					<EachNotificationSetting
						title={`WO #${nullSafeGetOrElse('workOrder.id', notification, '--')} (Subcontracted)`}
						description={`Email & mobile push notifications for this work order`}
						isSubscribed={isSubContractedWOSubscribed}
						workOrder={nullSafeGet('workOrder', notification)}
						saving={saving === NOTIFICATION_TYPE.subContractedWorkOrder}
						onChange={onSubscriptionToggle(NOTIFICATION_TYPE.subContractedWorkOrder)}
					/>
				) : null}
			</div>
		);
	}, [
		anyWOSubscribed,
		emailSubscribed,
		inAppSubscribed,
		isSubContractedWOSubscribed,
		isWOSubscribed,
		notification,
		notificationLabel,
		onSubscriptionToggle,
		saving,
		subContractedToWOAvailable,
		woAvailable,
	]);

	const getIconElement = useCallback(
		(iName, iColor) => (
			<div
				className="my-1 h-6 w-6 bg-cover bg-center"
				style={{
					WebkitMaskImage: `url('/${iName}.svg')`,
					WebkitMaskRepeat: 'no-repeat',
					WebkitMaskSize: 'contain',
					WebkitMaskPosition: 'center',
					maskImage: `url('/${iName}.svg')`,
					maskRepeat: 'no-repeat',
					maskSize: 'contain',
					maskPosition: 'center',
					backgroundColor: iColor,
				}}
			/>
		),
		[]
	);

	return (
		<Dropdown overlay={DropdownContent} className="ml-4" trigger={['click']}>
			<div key={1} onClick={stopDefaultEvents} className="border-1 group">
				<div className="flex flex-row items-center gap-2 rounded-md border border-solid border-[#D9D9D9] p-2">
					{getIconElement(notificationStatusProps.iconName, notificationStatusProps.iconColor)}
					{notificationStatusProps.icon2Name
						? getIconElement(notificationStatusProps.icon2Name, notificationStatusProps.iconColor)
						: null}
					<span className="bodymedium hidden flex-nowrap whitespace-nowrap group-hover:inline-flex ">
						{notificationStatusProps.label}
					</span>
					<DownOutlined className="h-4" />
				</div>
			</div>
		</Dropdown>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	settings:
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? state.supplier_settings.detail
			: state.buyer_settings.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	subscribeToWO: (obj, workOrder, isSubContractedFromWO) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isSubContractedFromWO
					? addSubSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)
					: addSupplierSubscribersForSuppliers(obj)(workOrder)
				: addBuyerSubscribersForBuyers(obj)(workOrder)
		),
	unsubscribeFromWO: (obj, workOrder, isSubContractedFromWO) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isSubContractedFromWO
					? removeSubSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)
					: removeSupplierSubscriberForSuppliers(obj)(workOrder)
				: removeBuyerSubscriberForBuyers(obj)(workOrder)
		),
	subscribeToSubContractedWO: (obj, workOrder) =>
		dispatch(addContractorSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)),
	unsubscribeToSubContractedWO: (obj, workOrder) =>
		dispatch(removeContractorSubscribers(ROLE_TYPES.SUPPLIER)(obj)(workOrder)),
	updateNotificaitonObjectInRedux: (data) =>
		dispatch(USER_NOTIFICATIONS_CRUD_ACTION_CREATORS.updatePartialSuccess(data)),
	updateSettings: (settings) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierSettingsRestCrudThunksForSupplier.update(settings)
				: buyerSettingsRestCrudThunksForBuyer.update(settings)
		),
	getWorkOrder: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrdersRestCrudThunksForSupplier.readOne(id)
				: workOrdersRestCrudThunksForBuyer.readOne(id)
		),
});

export default withUserType(
	connect(mapStateToProps, mapDispatchToProps)(NotificationSubscriptionHandler as any) as any
);
